import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, type Me, type Plan } from './lib/api.ts';
import { authClient } from './lib/auth.ts';
import type { Names } from './lib/describe.ts';
import { Link, navigate, usePath } from './lib/router.tsx';
import { Brand, Icon } from './ui.tsx';
import { SignIn, SignUp, Setup } from './screens/Auth.tsx';
import { Calendar } from './screens/Calendar.tsx';
import { PlanSetup } from './screens/PlanSetup.tsx';
import { Policies } from './screens/Policies.tsx';
import { Schedule } from './screens/Schedule.tsx';

type Session = { state: 'loading' } | { state: 'out' } | { state: 'setup'; me: Me } | { state: 'in'; me: Me; plan: Plan | null };

const NAV = [
  { href: '/schedule', label: 'Schedule', icon: 'schedule' },
  { href: '/calendar', label: 'Year calendar', icon: 'calendar' },
  { href: '/policies', label: 'Policies', icon: 'policies' },
];

export function App() {
  const [session, setSession] = useState<Session>({ state: 'loading' });
  const path = usePath();

  const refresh = useCallback(async () => {
    try {
      const me = await api.me();
      if (!me.setup.passkey || !me.setup.twoFactor) return setSession({ state: 'setup', me });
      const plans = await api.plans();
      const plan = plans[0] ? await api.plan(plans[0].id) : null;
      setSession({ state: 'in', me, plan });
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) setSession({ state: 'out' });
      else throw e;
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (session.state === 'loading') return <p className="loading">Opening your record…</p>;
  if (session.state === 'out') return path === '/signup' ? <SignUp onDone={refresh} /> : <SignIn onDone={refresh} />;
  if (session.state === 'setup') return <Setup me={session.me} onDone={refresh} />;
  if (!session.plan) return <PlanSetup onCreated={() => void refresh().then(() => navigate('/schedule'))} />;
  return <Shell me={session.me} plan={session.plan} path={path} onPlanChanged={refresh} />;
}

function Shell({ me, plan, path, onPlanChanged }: { me: Me; plan: Plan; path: string; onPlanChanged: () => void }) {
  const [menu, setMenu] = useState(false);
  const child = plan.children[0]?.name ?? 'your child';
  const names: Names = { parties: plan.parties, child };
  const current = NAV.find((n) => path.startsWith(n.href))?.href ?? '/schedule';

  useEffect(() => {
    if (!NAV.some((n) => path.startsWith(n.href))) navigate('/schedule', { replace: true });
  }, [path]);
  useEffect(() => setMenu(false), [path]);

  const signOut = () => void authClient.signOut().then(() => window.location.assign('/'));
  const measured = plan.parties.find((p) => p.id === plan.measuredPartyId)?.label;

  const nav = (
    <nav className="nav" aria-label="Main">
      {NAV.map((n) => (
        <Link key={n.href} href={n.href} aria-current={current === n.href ? 'page' : undefined}>
          <Icon name={n.icon} />
          {n.label}
        </Link>
      ))}
    </nav>
  );
  const foot = (
    <div className="side-foot">
      <div className="user">
        <span className="avatar">{me.user.name.slice(0, 1).toUpperCase()}</span>
        <div>
          {me.user.name}
          <small>{me.user.email}</small>
        </div>
      </div>
      <button className="btn btn-ghost" style={{ justifyContent: 'flex-start' }} onClick={signOut}>
        <Icon name="out" />
        Sign out
      </button>
    </div>
  );

  return (
    <div className="app">
      <aside className="sidebar" aria-label="Main">
        <Brand />
        <div className="scope">
          <b>{child}</b>
          <span>Measuring {measured}’s time</span>
        </div>
        {nav}
        {foot}
      </aside>
      <header className="topbar">
        <Brand size={22} />
        <button className="btn btn-ghost" aria-label="Menu" aria-expanded={menu} onClick={() => setMenu(true)}>
          <Icon name="menu" />
        </button>
      </header>
      {menu && (
        <div className="menu-sheet" role="dialog" aria-modal="true" aria-label="Menu">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <Brand size={22} />
            <button className="btn btn-ghost" aria-label="Close menu" onClick={() => setMenu(false)}>
              <Icon name="close" />
            </button>
          </div>
          {nav}
          {foot}
        </div>
      )}
      <main className="canvas">
        {current === '/schedule' && <Schedule plan={plan} names={names} onPlanChanged={onPlanChanged} />}
        {current === '/calendar' && <Calendar plan={plan} names={names} />}
        {current === '/policies' && <Policies plan={plan} />}
      </main>
    </div>
  );
}
